import React, { useCallback, useRef, useState } from 'react';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  appModalActionsSx, appModalCloseButtonSx, appModalHeaderDividerSx, appModalPaperSx, appModalPrimaryButtonSx,
  appModalPrimaryLoadingSx, appModalSecondaryButtonSx, appModalTitleBarSx, appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { colors } from '../../styles/theme';
import { useMetaAdsContext } from '../MetaAdsContext';
import {
  META_SHARE_PAUSE_ERROR, loadMetaCampaignsUsingShare, metaShareCutTexts, pauseMetaCampaigns,
} from '../metaShareGuard';
import MetaWriteNote, { metaNoteButtonSx } from './MetaWriteNote';

/*
 * Paso 2 (t1099u): antes de cortar un link de compartir con campañas de Terrio que llevan ahí, una
 * confirmación: cuántas son, qué ve quien toque el anuncio, y «Pausar y seguir» (pause de F2a de las
 * activas) / «Seguir igual» / «Cancelar». Ver `metaShareGuard`.
 */

const textButtonSx = { ...metaNoteButtonSx, fontSize: '14px', color: colors.textSecondary, mr: 'auto', '&:hover': { bgcolor: 'transparent', color: '#FBFBFB', textDecoration: 'underline' } };

/**
 * @param {{ state: { texts: object, campaigns: object[] }|null, pausing: boolean, error: string|null,
 *   onPause: () => void, onContinue: () => void, onCancel: () => void }} props
 */
export function MetaShareCutDialog({ state, pausing = false, error = null, onPause, onContinue, onCancel }) {
  if (!state) return null;
  const { texts } = state;
  return (
    <Dialog
      open
      onClose={pausing ? undefined : onCancel}
      maxWidth={false}
      PaperProps={{ sx: { ...appModalPaperSx, width: 'min(460px, 95vw)', maxWidth: '460px', height: 'auto' }, 'data-testid': 'meta-share-cut-dialog' }}
      aria-labelledby="meta-share-cut-title"
    >
      <DialogTitle id="meta-share-cut-title" component="div" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>{texts.title}</Typography>
        <IconButton size="small" onClick={onCancel} disabled={pausing} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent sx={{ px: '20px', pt: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={{ fontSize: '14px', lineHeight: '20px', color: '#E3E3E3' }} data-testid="meta-share-cut-text">{texts.text}</Typography>
        {error ? <MetaWriteNote tone="error" testId="meta-share-cut-error">{error}</MetaWriteNote> : null}
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        {texts.pauseLabel ? (
          <Button variant="text" onClick={onContinue} disabled={pausing} sx={textButtonSx} data-testid="meta-share-cut-continue">
            {texts.continueLabel}
          </Button>
        ) : null}
        <Button variant="outlined" onClick={onCancel} disabled={pausing} sx={appModalSecondaryButtonSx} data-testid="meta-share-cut-cancel">
          Cancelar
        </Button>
        {texts.pauseLabel ? (
          <Button
            variant="contained"
            onClick={onPause}
            disabled={pausing}
            sx={pausing ? { ...appModalPrimaryButtonSx, ...appModalPrimaryLoadingSx } : appModalPrimaryButtonSx}
            startIcon={pausing ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
            data-testid="meta-share-cut-pause"
          >
            {texts.pauseLabel}
          </Button>
        ) : (
          <Button variant="contained" onClick={onContinue} sx={appModalPrimaryButtonSx} data-testid="meta-share-cut-continue">
            {texts.continueLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Guarda de los diálogos de compartir (unidad / lote y showroom). Solo actúa para el dueño en su espacio
 * con Meta en MapPage (`MetaAdsProvider`); si no, o si no hay campañas de Terrio con ese link, o si no se
 * pudieron leer, deja seguir sin preguntar.
 *
 * `confirmCut(scope, mode)` → `Promise<boolean>` (true = seguir con el corte).
 * `scope`: `{ userLotIds }` (todas las unidades del lote, o las que se sacan) o `{ projectId }`.
 * `mode`: `unshare` | `restrict` | `remove_units`. Renderizar `guardDialog`.
 *
 * @param {{ enabled?: boolean }} [options] `enabled: false` en espacio ajeno (el invitado no pausa).
 */
export function useMetaShareCutGuard({ enabled = true } = {}) {
  const meta = useMetaAdsContext();
  const mc = meta?.metaConnection || null;
  const connection = mc?.connection || null;
  const active = Boolean(enabled && mc?.loaded && !mc.hidden && connection && !mc.ownerUserId && connection.viewerRole === 'owner');
  const [state, setState] = useState(null);
  const [pausing, setPausing] = useState(false);
  const [error, setError] = useState(null);
  const resolveRef = useRef(null);
  const invalidateCampaigns = meta?.invalidateCampaigns;

  const finish = useCallback((value) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setState(null);
    setPausing(false);
    setError(null);
    resolve?.(value);
  }, []);

  const confirmCut = useCallback(async (scope, mode = 'unshare') => {
    if (!active) return true;
    let campaigns = null;
    try {
      campaigns = await loadMetaCampaignsUsingShare(scope);
    } catch {
      campaigns = null;
    }
    if (!campaigns || campaigns.length === 0) return true;
    resolveRef.current?.(false);
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setError(null);
      setPausing(false);
      setState({ campaigns, mode, texts: metaShareCutTexts(campaigns, mode) });
    });
  }, [active]);

  const onPause = async () => {
    if (!state || pausing) return;
    setPausing(true);
    setError(null);
    const ids = state.campaigns.filter((c) => c.canPause).map((c) => c.id);
    const { paused, failed } = await pauseMetaCampaigns(ids);
    if (paused.length) invalidateCampaigns?.();
    if (failed.length) {
      // Quedan solo las que fallaron: «Pausar» reintenta esas; «Seguir igual» corta igual.
      const left = state.campaigns.filter((c) => !paused.includes(c.id));
      setState({ ...state, campaigns: left, texts: metaShareCutTexts(left, state.mode) });
      setError(META_SHARE_PAUSE_ERROR);
      setPausing(false);
      return;
    }
    finish(true);
  };

  const guardDialog = (
    <MetaShareCutDialog
      state={state}
      pausing={pausing}
      error={error}
      onPause={onPause}
      onContinue={() => finish(true)}
      onCancel={() => finish(false)}
    />
  );
  return { confirmCut, guardDialog, guardActive: active };
}

export default MetaShareCutDialog;
